import {useMemo, useState} from 'react'
import {
  Box,
  Typography,
  Stack,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material'
import {styled} from '@mui/system'
import {
  BarChart as BarChartRe,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import MaterialTable from '../../components/MaterialTable'
import {BarChart} from '../../components/charts/BarChart'
import ChartTooltip from '../../components/charts/ChartTooltip'
import {
  SummaryCard,
  SummaryCardValue,
  SummaryCardLabel,
  SummaryCardSubtext,
  ChartCard,
  FilterBar,
} from './PerformanceStyled'
import {PerformanceLegend} from './PerformanceLegend'
import {
  generateOverallForwardColumns,
  generateForwardColumnGroups,
  computeWeightedAverageRow,
} from './performanceColumns'
import {formatNumber, numberPrecision} from '../../helpers/UtilityHelper'

// ─── Local Styled Components ────────────────────────────────────────────────
const SectionRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3),
  alignItems: 'stretch',
  flexWrap: 'wrap',
}))

const ChartHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
}))

const ChartTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.88rem',
  color: '#1a2b3c',
  lineHeight: 1.3,
})

const ChartSubtitle = styled(Typography)({
  fontSize: '0.7rem',
  color: '#637381',
  marginTop: 2,
})

const EmptyChart = styled(Typography)({
  padding: '48px 0',
  textAlign: 'center',
  color: '#9ca3af',
  fontSize: '0.82rem',
})

const StyledToggleButton = styled(ToggleButton)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  padding: '4px 12px',
})

const TableSection = styled(Box)({
  '& .MuiTableContainer-root': {
    borderRadius: 0,
  },
})

// ─── Helpers ────────────────────────────────────────────────────────────────
const matchesVolumeRange = (volume, range) => {
  if (range === 'all') return true
  if (range === '<1000') return volume < 1000
  if (range === '>1000') return volume >= 1000
  if (range === '>5000') return volume >= 5000
  if (range === '>20000') return volume >= 20000
  if (range === '>50000') return volume >= 50000
  if (range === '>100000') return volume >= 100000
  return true
}

const volumeRangeOptions = [
  {value: 'all', label: 'All'},
  {value: '<1000', label: '< 1,000'},
  {value: '>1000', label: '> 1,000'},
  {value: '>5000', label: '> 5,000'},
  {value: '>20000', label: '> 20,000'},
  {value: '>50000', label: '> 50,000'},
  {value: '>100000', label: '> 1 Lakh'},
]

const getBarColor = (index, total) => {
  const topCutoff = Math.ceil(total * 0.3)
  const midCutoff = Math.ceil(total * 0.6)
  if (index < topCutoff) return '#10b981'
  if (index < midCutoff) return '#f59e0b'
  return '#ef4444'
}

const inferCourierMode = (name) => {
  const lower = (name || '').toLowerCase()
  if (lower.includes('air')) return 'air'
  if (lower.includes('surface') || lower.includes('sfc')) return 'surface'
  return 'other'
}

export function OverallForward({data, courierPerformance}) {
  const [volumeRange, setVolumeRange] = useState('all')
  const [selectedCourierNames, setSelectedCourierNames] = useState([])
  const [showGroups, setShowGroups] = useState(['delivery', 'tat', 'volume'])
  const [excludeDirectCP, setExcludeDirectCP] = useState(false)
  const [courierTypeFilter, setCourierTypeFilter] = useState('all')

  const handleShowGroupChange = (_, newGroups) => {
    if (newGroups.length > 0) setShowGroups(newGroups)
  }

  const visibility = useMemo(
    () => ({
      showDelivery: showGroups.includes('delivery'),
      showTAT: showGroups.includes('tat'),
      showVolume: showGroups.includes('volume'),
    }),
    [showGroups]
  )

  const filteredData = useMemo(() => {
    if (!data) return []
    let filtered = [...data]
    if (excludeDirectCP) {
      filtered = filtered.filter((item) => !item.name?.toLowerCase().includes('direct'))
    }
    if (courierTypeFilter !== 'all') {
      if (courierTypeFilter === 'sdd_ndd') {
        filtered = filtered.filter((item) => item.isSddNddEnabled)
      } else {
        filtered = filtered.filter((item) => inferCourierMode(item.name) === courierTypeFilter)
      }
    }
    if (volumeRange !== 'all') {
      filtered = filtered.filter((item) =>
        matchesVolumeRange(item.totalPickedupCount || 0, volumeRange)
      )
    }
    return filtered
  }, [data, volumeRange, excludeDirectCP, courierTypeFilter])

  const sortedData = useMemo(() => {
    const sorted = [...filteredData]
    sorted.sort((a, b) => (b.totalPickedupCount || 0) - (a.totalPickedupCount || 0))
    return sorted.filter(
      (item) =>
        item.courierId &&
        (selectedCourierNames.length > 0 ? selectedCourierNames.includes(item.name) : true)
    )
  }, [filteredData, selectedCourierNames])

  const trendData = useMemo(() => {
    const trendMap = new Map()
    const trend = courierPerformance?.deliveryPerformanceTrend
    if (!trend || !Array.isArray(trend)) return trendMap
    for (const entry of trend) {
      if (!entry.data) continue
      const entries = entry.data instanceof Map ? entry.data.entries() : Object.entries(entry.data)
      for (const [courierId, perf] of entries) {
        if (!trendMap.has(courierId)) trendMap.set(courierId, [])
        trendMap.get(courierId).push(perf.overallDelivery || 0)
      }
    }
    return trendMap
  }, [courierPerformance])

  const {monthNames, monthlyData} = useMemo(() => {
    const deliveryTrend = courierPerformance?.deliveryPerformanceTrend
    const tatTrend = courierPerformance?.tatPerformanceTrend
    const names = []
    const mData = new Map()
    if (!deliveryTrend || !Array.isArray(deliveryTrend) || deliveryTrend.length <= 1) {
      return {monthNames: names, monthlyData: mData}
    }
    for (const entry of deliveryTrend) {
      if (entry.name) names.push(entry.name)
    }
    for (const entry of deliveryTrend) {
      if (!entry.data || !entry.name) continue
      const monthKey = entry.name
      const entries = entry.data instanceof Map ? entry.data.entries() : Object.entries(entry.data)
      for (const [courierId, perf] of entries) {
        if (!mData.has(courierId)) mData.set(courierId, {})
        const courierMonths = mData.get(courierId)
        courierMonths[monthKey] = {...(courierMonths[monthKey] || {}), ...perf}
      }
    }
    if (tatTrend && Array.isArray(tatTrend)) {
      for (const entry of tatTrend) {
        if (!entry.data || !entry.name) continue
        const monthKey = entry.name
        const entries = entry.data instanceof Map ? entry.data.entries() : Object.entries(entry.data)
        for (const [courierId, perf] of entries) {
          if (!mData.has(courierId)) mData.set(courierId, {})
          const courierMonths = mData.get(courierId)
          courierMonths[monthKey] = {...(courierMonths[monthKey] || {}), ...perf}
        }
      }
    }
    return {monthNames: names, monthlyData: mData}
  }, [courierPerformance])

  const weightedAvgRow = useMemo(() => computeWeightedAverageRow(sortedData), [sortedData])
  const tableRows = useMemo(() => {
    if (!weightedAvgRow) return sortedData
    return [weightedAvgRow, ...sortedData]
  }, [weightedAvgRow, sortedData])

  const summaryData = useMemo(() => {
    const valid = sortedData.filter((d) => d.courierId)
    if (valid.length === 0) return {avgDelivery: 0, avgFirstAttempt: 0, best: null, worst: null}
    const totalVol = valid.reduce((s, d) => s + (d.totalPickedupCount || 0), 0)
    const avgDelivery = totalVol > 0 ? valid.reduce((s, d) => s + (d.overallDelivery || 0) * (d.totalPickedupCount || 0), 0) / totalVol : 0
    const totalAttempted = valid.reduce((s, d) => s + (d.totalDeliveryAttemptedCount || 0), 0)
    const avgFirstAttempt = totalAttempted > 0 ? valid.reduce((s, d) => s + (d.firstDeliveryAttemptInTATPercentage || 0) * (d.totalDeliveryAttemptedCount || 0), 0) / totalAttempted : 0
    const best = valid.reduce((best, d) => (d.overallDelivery || 0) > (best?.overallDelivery || 0) ? d : best, valid[0])
    const worst = valid.reduce((worst, d) => (d.overallDelivery || 0) < (worst?.overallDelivery || 0) ? d : worst, valid[0])
    return {avgDelivery, avgFirstAttempt, best, worst}
  }, [sortedData])

  const top10ChartData = useMemo(() => {
    return sortedData.filter((d) => d.courierId).slice(0, 10).map((d) => ({
      name: d.name,
      delivery: Number(numberPrecision(d.overallDelivery || 0, 1)),
    }))
  }, [sortedData])

  const codVsPrepaidData = useMemo(() => {
    return [...sortedData]
      .filter((d) => d.courierId)
      .sort((a, b) => (b.totalPickedupCount || 0) - (a.totalPickedupCount || 0))
      .slice(0, 5)
      .map((d) => ({
        name: d.name,
        COD: numberPrecision(d.codDeliveryPercentage || 0, 1),
        Prepaid: numberPrecision(d.prepaidDeliveryPercentage || 0, 1),
      }))
  }, [sortedData])

  const hasTrend = trendData.size > 0

  const columns = useMemo(
    () => generateOverallForwardColumns(sortedData, visibility, trendData, monthNames, monthlyData),
    [sortedData, visibility, trendData, monthNames, monthlyData]
  )

  const columnGroups = useMemo(
    () => generateForwardColumnGroups(visibility, hasTrend, monthNames),
    [visibility, hasTrend, monthNames]
  )

  const courierNameOptions = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({value: item.name, label: item.name}))
  }, [data])

  const totalShipments = useMemo(() => {
    return sortedData.filter((d) => d.courierId).reduce((s, d) => s + (d.totalPickedupCount || 0), 0)
  }, [sortedData])

  return (
    <Box>
      {/* KPI Cards */}
      <SectionRow>
        <SummaryCard borderColor='#4361ee'>
          <SummaryCardLabel>Total Shipments</SummaryCardLabel>
          <SummaryCardValue>{formatNumber(totalShipments)}</SummaryCardValue>
          <SummaryCardSubtext>{sortedData.filter((d) => d.courierId).length} service types</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#0d9488'>
          <SummaryCardLabel>Avg Delivery %</SummaryCardLabel>
          <SummaryCardValue>{numberPrecision(summaryData.avgDelivery, 1)}%</SummaryCardValue>
          <SummaryCardSubtext>Weighted by volume</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#10b981'>
          <SummaryCardLabel>Avg 1st Attempt</SummaryCardLabel>
          <SummaryCardValue>{numberPrecision(summaryData.avgFirstAttempt, 1)}%</SummaryCardValue>
          <SummaryCardSubtext>In TAT adherence</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#f59e0b'>
          <SummaryCardLabel>Best Performer</SummaryCardLabel>
          <SummaryCardValue>{summaryData.best ? numberPrecision(summaryData.best.overallDelivery, 1) + '%' : '-'}</SummaryCardValue>
          <SummaryCardSubtext>{summaryData.best?.name || '-'}</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#ef4444'>
          <SummaryCardLabel>Worst Performer</SummaryCardLabel>
          <SummaryCardValue>{summaryData.worst ? numberPrecision(summaryData.worst.overallDelivery, 1) + '%' : '-'}</SummaryCardValue>
          <SummaryCardSubtext>{summaryData.worst?.name || '-'}</SummaryCardSubtext>
        </SummaryCard>
      </SectionRow>

      {/* Charts */}
      <Stack direction='row' spacing={2} sx={{px: 3, pb: 2.5}}>
        <ChartCard sx={{flex: 6}}>
          <ChartHeader>
            <Box>
              <ChartTitle>Top 10 Service Types — Overall Delivery %</ChartTitle>
              <ChartSubtitle>Green = top 30% | Amber = mid 30% | Red = bottom 40%</ChartSubtitle>
            </Box>
            <Chip label={`${top10ChartData.length} service types`} size='small' variant='outlined' sx={{fontSize: '0.68rem', height: 22}} />
          </ChartHeader>
          {top10ChartData.length > 0 ? (
            <ResponsiveContainer width='100%' height={340}>
              <BarChartRe data={top10ChartData} layout='vertical' margin={{left: 10, right: 20}}>
                <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' horizontal={false} />
                <XAxis type='number' fontSize={11} fontWeight={500} unit='%' domain={[0, 100]} />
                <YAxis dataKey='name' type='category' fontSize={11} fontWeight={500} width={140} tick={{fill: '#374151'}} />
                <Tooltip content={(props) => <ChartTooltip {...props} />} cursor={{fill: 'rgba(0,0,0,0.03)'}} />
                <Bar dataKey='delivery' name='Delivery %' maxBarSize={22} unit='%' radius={[0, 4, 4, 0]} animationDuration={800}>
                  {top10ChartData.map((_, idx) => (
                    <Cell key={idx} fill={getBarColor(idx, top10ChartData.length)} />
                  ))}
                </Bar>
              </BarChartRe>
            </ResponsiveContainer>
          ) : (
            <EmptyChart>No data available</EmptyChart>
          )}
        </ChartCard>

        <ChartCard sx={{flex: 4}}>
          <ChartHeader>
            <Box>
              <ChartTitle>COD vs Prepaid — Delivery %</ChartTitle>
              <ChartSubtitle>Top 5 service types by volume</ChartSubtitle>
            </Box>
          </ChartHeader>
          {codVsPrepaidData.length > 0 ? (
            <BarChart
              data={codVsPrepaidData}
              height={340}
              colors={null}
              tooltipContent={null}
              chartOptions={{
                xaxis: 'name',
                data: [
                  {dataKey: 'COD', label: 'COD %', fill: '#f59e0b', unit: '%'},
                  {dataKey: 'Prepaid', label: 'Prepaid %', fill: '#10b981', unit: '%'},
                ],
              }}
            />
          ) : (
            <EmptyChart>No data available</EmptyChart>
          )}
        </ChartCard>
      </Stack>

      {/* Filters */}
      <FilterBar>
        <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
          <Autocomplete
            multiple
            sx={{minWidth: 280}}
            size='small'
            value={courierNameOptions.filter((o) => selectedCourierNames.includes(o.value))}
            onChange={(_event, values) => setSelectedCourierNames(values.map((v) => v.value))}
            options={courierNameOptions}
            getOptionLabel={(option) => option.label}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({index})} key={option.value} label={option.label} size='small' sx={{maxWidth: 140, fontSize: '0.72rem'}} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label='Filter Service Types' size='small' placeholder='Select...' />
            )}
          />
          <FormControl size='small' sx={{minWidth: 140}}>
            <InputLabel>Mode</InputLabel>
            <Select value={courierTypeFilter} label='Mode' onChange={(e) => setCourierTypeFilter(e.target.value)}>
              <MenuItem value='all'>All Modes</MenuItem>
              <MenuItem value='surface'>Surface</MenuItem>
              <MenuItem value='air'>Air</MenuItem>
            </Select>
          </FormControl>
          <FormControl size='small' sx={{minWidth: 130}}>
            <InputLabel>Volume</InputLabel>
            <Select value={volumeRange} label='Volume' onChange={(e) => setVolumeRange(e.target.value)}>
              {volumeRangeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size='small' checked={excludeDirectCP} onChange={(e) => setExcludeDirectCP(e.target.checked)} />}
            label={<Typography variant='body2' sx={{fontSize: '0.75rem', fontWeight: 500}}>Exclude Direct</Typography>}
          />
        </Stack>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <ToggleButtonGroup value={showGroups} onChange={handleShowGroupChange} size='small' color='primary'>
            <StyledToggleButton value='delivery'>Delivery</StyledToggleButton>
            <StyledToggleButton value='tat'>TAT</StyledToggleButton>
            <StyledToggleButton value='volume'>Volume</StyledToggleButton>
          </ToggleButtonGroup>
          <PerformanceLegend />
        </Stack>
      </FilterBar>

      {/* Table */}
      <TableSection>
        <MaterialTable
          rows={tableRows}
          columns={columns}
          columnGroups={columnGroups}
          exportExcel={false}
          reportName='COURIER_PERFORMANCE'
          size='medium'
          itemsPerPage={100}
          frozenColumnsCount={2}
          minWidth={(visibility.showDelivery && visibility.showTAT ? 1800 : 900) + (monthNames.length > 1 ? monthNames.length * 600 : 0)}
          noDataMessage='No courier data available'
        />
      </TableSection>
    </Box>
  )
}
